const WISHLIST_CONTROL_SELECTORS = [
  '[data-wishlist-button]',
  '[data-wishlist-product] button',
  '.Vtl-WishlistButton__WishlistIconButton',
  'button[class*="wishlist" i]',
  'a[class*="wishlist" i]',
  '.Vtl-WishlistIcon__Wrapper button',
  '.Vtl-WishlistIcon__ProductPageWrapper button',
].join(',');

const APP_SURFACE_SELECTORS = [
  '[data-wishlist-page]',
  '[class*="page-wishlist" i]',
  '[id*="wishlist-page" i]',
  '[class*="wishlist-page" i]',
  '[id*="wishlist-container" i]',
  '[class*="wishlist-container" i]',
].join(',');

const SQUARE_WISHLIST_BUTTON_SELECTOR = '.Vtl-WishlistButton__WishlistIconButton';
const SQUARE_WISHLIST_INTERACTION_SELECTOR = [
  '.Vtl-WishlistButton__WishlistIconButton',
  '.Vtl-WishlistIcon__Wrapper',
  '.Vtl-WishlistIcon__ProductPageWrapper',
  '.Vtl-WishlistProduct__CloseButton',
].join(',');
const SQUARE_WISHLIST_COUNTER_SELECTOR = '.Vtl-WishlistHeader__HeartCounter';
const SQUARE_WISHLIST_ADD_TO_CART_SELECTOR = '.Vtl-WishlistProduct__AddToCart';
const SQUARE_WISHLIST_REMOVE_SELECTOR = '.Vtl-WishlistProduct__CloseButton';
const THEME_WISHLIST_LINK_SELECTOR = '.es-wishlist-link, .es-drawer-personal-action[href*="wishlist"]';
const WISHLIST_MUTATION_SELECTOR = [
  '.vtl-proxy-page-wishlist',
  '.Vtl-WishlistHeaderWrapper',
  '.Vtl-WishlistHeader__HeartCounter',
  '.Vtl-WishlistPage',
  '.Vtl-WishlistProduct',
  '.Vtl-WishlistButton__WishlistIconButton',
  '.Vtl-WishlistIcon__Wrapper',
  '.Vtl-WishlistIcon__ProductPageWrapper',
].join(',');
const WISHLIST_COUNT_STORAGE_KEY = 'esmeralda:wishlist-count:v1';
let pendingWishlistCount = null;
let cartCountSyncSequence = 0;

function isThemeWishlistLink(element) {
  return Boolean(element.closest('.es-wishlist-link, .es-drawer-personal-action'));
}

function getWishlistCountFromSquare() {
  const counter = document.querySelector(SQUARE_WISHLIST_COUNTER_SELECTOR);
  const parsedCount = Number.parseInt(counter?.textContent?.trim() || '', 10);
  if (Number.isFinite(parsedCount)) return Math.max(0, parsedCount);

  const wishlistPage = document.querySelector('.Vtl-WishlistPage');
  const renderedProducts = wishlistPage?.querySelectorAll('.Vtl-WishlistProduct').length ?? 0;
  if (renderedProducts > 0) return renderedProducts;

  // Square mounts an empty wrapper before its saved products hydrate. Only its
  // completed empty-state view is authoritative evidence of a zero count.
  return document.querySelector('.Vtl-WishlistPage__EmptyList') ? 0 : null;
}

function getStoredWishlistCount() {
  try {
    const stored = JSON.parse(localStorage.getItem(WISHLIST_COUNT_STORAGE_KEY) || 'null');
    const count = Number(stored?.value);
    return Number.isFinite(count) ? Math.max(0, count) : null;
  } catch (_) {
    return null;
  }
}

function storeWishlistCount(count) {
  try {
    localStorage.setItem(WISHLIST_COUNT_STORAGE_KEY, JSON.stringify({
      value: count,
      timestamp: Date.now(),
    }));
  } catch (_) {
    // Storage can be unavailable in strict privacy modes; the live badge still works.
  }
}

function ensureWishlistBadge(link) {
  let badge = link.querySelector(':scope > .es-wishlist-count');
  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'es-wishlist-count';
    badge.setAttribute('aria-hidden', 'true');
    badge.hidden = true;
    badge.append(document.createTextNode(''));
    link.append(badge);
  }
  return badge;
}

function setWishlistCount(count, pulse = false, persist = true) {
  const safeCount = Math.max(0, Number(count) || 0);
  const visibleCount = safeCount === 0 ? '' : safeCount > 99 ? '99+' : String(safeCount);

  if (persist) storeWishlistCount(safeCount);

  document.querySelectorAll(THEME_WISHLIST_LINK_SELECTOR).forEach((link) => {
    const badge = ensureWishlistBadge(link);
    const textNode = badge.firstChild;
    if (textNode) textNode.nodeValue = visibleCount;
    badge.hidden = safeCount === 0;
    link.setAttribute('aria-label', safeCount === 1 ? 'Wishlist, 1 saved item' : `Wishlist, ${safeCount} saved items`);

    if (pulse && safeCount > 0) {
      badge.classList.remove('es-wishlist-count--pulse');
      requestAnimationFrame(() => badge.classList.add('es-wishlist-count--pulse'));
    }
  });
}

function syncWishlistCount() {
  const count = getWishlistCountFromSquare();
  if (count === null) {
    const storedCount = getStoredWishlistCount();
    if (storedCount !== null) setWishlistCount(storedCount, false, false);
    return;
  }

  if (pendingWishlistCount) {
    if (count === pendingWishlistCount.value) {
      pendingWishlistCount = null;
    } else if (Date.now() < pendingWishlistCount.expiresAt) {
      setWishlistCount(pendingWishlistCount.value);
      return;
    } else {
      pendingWishlistCount = null;
    }
  }

  setWishlistCount(count);
}

function makeWishlistControlAccessible(control) {
  if (control.matches('button, a, input, [role="button"]')) return;
  control.setAttribute('role', 'button');
  control.setAttribute('tabindex', '0');

  if (control.dataset.esWishlistKeyboardBound === 'true') return;
  control.dataset.esWishlistKeyboardBound = 'true';
  control.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    control.click();
  });
}

function isolateWishlistPointerGestures(surface) {
  if (surface.dataset.esWishlistPointerIsolated === 'true') return;
  surface.dataset.esWishlistPointerIsolated = 'true';

  // Product carousels interpret the pointer sequence before the resulting
  // click. Keep those gestures inside Square's visible heart without
  // cancelling the click Square uses to save or remove the product.
  [
    'pointerdown',
    'pointerup',
    'pointercancel',
    'touchstart',
    'touchend',
    'mousedown',
    'mouseup',
    'dragstart',
    'selectstart',
  ].forEach((eventName) => {
    surface.addEventListener(eventName, (event) => {
      event.stopPropagation();
      if (eventName === 'dragstart' || eventName === 'selectstart') event.preventDefault();
    }, { passive: eventName.startsWith('touch') });
  });
}

function brandWishlistPageProducts(root = document) {
  root.querySelectorAll('.Vtl-WishlistProduct__CloseButton').forEach((removeButton) => {
    makeWishlistControlAccessible(removeButton);
    removeButton.setAttribute('aria-label', 'Remove from wishlist');
    removeButton.setAttribute('title', 'Remove from wishlist');

    if (removeButton.dataset.esWishlistBranded === 'true') return;
    removeButton.dataset.esWishlistBranded = 'true';

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z');
    icon.append(path);
    removeButton.replaceChildren(icon);
  });
}

function hideWishlistBulkAddToCart(root = document) {
  root.querySelectorAll('.Vtl-WishlistPage button, .Vtl-WishlistPage a, .Vtl-WishlistPage [role="button"]').forEach((control) => {
    const label = (control.textContent || control.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim();
    if (!/^add all to cart$/i.test(label)) return;

    const wishlistPage = control.closest('.Vtl-WishlistPage');
    let target = control;
    while (target.parentElement && target.parentElement !== wishlistPage) {
      const parentLabel = (target.parentElement.textContent || '').replace(/\s+/g, ' ').trim();
      if (!/^add all to cart$/i.test(parentLabel)) break;
      target = target.parentElement;
    }

    target.hidden = true;
    target.setAttribute('aria-hidden', 'true');
    target.style.setProperty('display', 'none', 'important');
  });
}

function connectWishlistControls(root = document) {
  hideWishlistBulkAddToCart(root);

  root.querySelectorAll([
    '.Vtl-WishlistButton__WishlistIconButton',
    '.Vtl-WishlistIcon__Wrapper',
    '.Vtl-WishlistIcon__ProductPageWrapper',
  ].join(',')).forEach(isolateWishlistPointerGestures);

  root.querySelectorAll(WISHLIST_CONTROL_SELECTORS).forEach((control) => {
    // Card markers contain "wishlist" too; never brand Shopify purchase controls.
    if (control.closest('quick-add-component, product-form-component')) return;
    if (isThemeWishlistLink(control)) return;

    if (control.matches(SQUARE_WISHLIST_BUTTON_SELECTOR)) makeWishlistControlAccessible(control);

    const header = control.closest('#header-group, header-component');
    if (header) {
      control.classList.add('es-square-wishlist-duplicate');
      return;
    }

    const card = control.closest('product-card, .es-product-card');
    if (card) {
      card.classList.add('es-has-wishlist-control');
      control.classList.add('es-square-wishlist-control', 'es-square-wishlist-control--card');
      return;
    }

    const productDetails = control.closest('.product-information, .product-details, [data-product-information]');
    if (productDetails) {
      control.classList.add('es-square-wishlist-control', 'es-square-wishlist-control--product');
    }
  });

  root.querySelectorAll(APP_SURFACE_SELECTORS).forEach((surface) => {
    if (surface.closest('#header-group')) return;

    // Vitals initializes the app-proxy target by its original class signature.
    // Do not decorate that empty target until Square has hydrated its page;
    // the mutation observer will brand it as soon as the product UI appears.
    const isUnhydratedProxy = surface.matches('.vtl-proxy-page-wishlist')
      && !surface.querySelector('.Vtl-WishlistPage');
    if (isUnhydratedProxy) {
      surface.classList.remove('es-square-wishlist-surface');
      return;
    }

    surface.classList.add('es-square-wishlist-surface');
  });

  document.querySelectorAll('.Vtl-WishlistPage__EmptyList').forEach((emptyState) => {
    if (emptyState.querySelector('.es-wishlist-empty-action')) return;
    const action = document.createElement('a');
    action.className = 'es-wishlist-empty-action';
    action.href = '/collections/all';
    action.textContent = 'Explore the collection';
    emptyState.append(action);
  });

  brandWishlistPageProducts(root);

  document.querySelectorAll(THEME_WISHLIST_LINK_SELECTOR).forEach(ensureWishlistBadge);
  syncWishlistCount();
}

let wishlistRefreshQueued = false;
function queueWishlistRefresh() {
  if (wishlistRefreshQueued) return;
  wishlistRefreshQueued = true;
  window.setTimeout(() => {
    wishlistRefreshQueued = false;
    connectWishlistControls();
  }, 64);
}

function queueCartCountSync() {
  const sequence = ++cartCountSyncSequence;

  // Give immediate feedback, then reconcile against Shopify. The cart icon
  // retains this optimistic floor while Square's request is in flight, so an
  // early /cart.js response cannot make the badge flash back to its old value.
  document.querySelectorAll('cart-icon').forEach((cartIcon) => cartIcon.incrementOptimisticCartCount?.(1));

  [400, 1000, 2200, 5200].forEach((delay) => {
    window.setTimeout(() => {
      if (sequence !== cartCountSyncSequence) return;
      document.querySelectorAll('cart-icon').forEach((cartIcon) => cartIcon.syncCartCount?.());
    }, delay);
  });
}

function handleWishlistClick(event) {
  if (!(event.target instanceof Element)) return;

  if (event.target.closest(SQUARE_WISHLIST_ADD_TO_CART_SELECTOR)) {
    queueCartCountSync();
    return;
  }

  // Shopify product cards and product media use full-surface links/zoom
  // handlers. Cancel their default action without stopping propagation so
  // Square's own add/remove listener still receives the same tap.
  const wishlistInteraction = event.target.closest(SQUARE_WISHLIST_INTERACTION_SELECTOR);
  if (wishlistInteraction) event.preventDefault();

  const removeButton = event.target.closest(SQUARE_WISHLIST_REMOVE_SELECTOR);
  if (removeButton) {
    const currentCount = getWishlistCountFromSquare() ?? getStoredWishlistCount() ?? 0;
    const nextCount = Math.max(0, currentCount - 1);
    pendingWishlistCount = { value: nextCount, expiresAt: Date.now() + 3000 };
    setWishlistCount(nextCount, true);
    [250, 700, 1600, 3200].forEach((delay) => window.setTimeout(syncWishlistCount, delay));
    return;
  }

  const control = event.target.closest(SQUARE_WISHLIST_BUTTON_SELECTOR);
  if (!control || control.closest('#header-group')) return;

  const currentCount = getWishlistCountFromSquare() ?? getStoredWishlistCount() ?? 0;
  const isCurrentlySaved = control.getAttribute('title')?.toLowerCase().includes('added') || false;
  const nextCount = Math.max(0, currentCount + (isCurrentlySaved ? -1 : 1));
  pendingWishlistCount = { value: nextCount, expiresAt: Date.now() + 3000 };
  setWishlistCount(nextCount, true);

  // Reconcile the optimistic badge with Square's authoritative counter.
  [250, 700, 1600, 3200].forEach((delay) => window.setTimeout(syncWishlistCount, delay));
}

function handleWishlistMutations(mutations) {
  const wishlistChanged = mutations.some((mutation) => {
    const targetElement = mutation.target instanceof Element
      ? mutation.target
      : mutation.target.parentElement;

    if (targetElement?.closest('.es-wishlist-count')) return false;
    if (targetElement?.closest(WISHLIST_MUTATION_SELECTOR)) return true;

    return [...mutation.addedNodes].some((node) => {
      if (!(node instanceof Element)) return false;
      return node.matches(WISHLIST_MUTATION_SELECTOR)
        || Boolean(node.querySelector(WISHLIST_MUTATION_SELECTOR));
    });
  });

  if (wishlistChanged) queueWishlistRefresh();
}

const cachedWishlistCount = getStoredWishlistCount();
if (cachedWishlistCount !== null) setWishlistCount(cachedWishlistCount, false, false);
connectWishlistControls();

document.addEventListener('click', handleWishlistClick, true);

new MutationObserver(handleWishlistMutations).observe(document.documentElement, {
  childList: true,
  characterData: true,
  subtree: true,
});

document.addEventListener('shopify:section:load', queueWishlistRefresh);

window.addEventListener('storage', (event) => {
  if (event.key !== WISHLIST_COUNT_STORAGE_KEY) return;
  const storedCount = getStoredWishlistCount();
  if (storedCount !== null) setWishlistCount(storedCount, false, false);
});
